/**
 * Dashboard break-even per unità organizzativa.
 *
 * Card con costi fissi, fatturato realizzato, barra progresso
 * colorata e fatturato giornaliero necessario.
 * Formattazione via useFormatters() (DEC-028/DEC-030).
 *
 * Importa esclusivamente da @floraltech/ui (regola 6.8 CLAUDE.md).
 */

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@floraltech/ui';
import { TrendingUp } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { useFormatters } from '@lib/formatters';
import { useUO } from '@shell/UOContext';

/* ─────────────────────────── Utility ─────────────────────────── */

const authHeadersGet = (): Record<string, string> => ({
  Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
});

/* ─────────────────────────── Interfacce ─────────────────────────── */

interface Unita {
  id: string;
  nome: string;
}

interface BreakEvenData {
  totale_costi_fissi_mensili: string;
  fatturato_realizzato: string;
  fatturato_necessario: string;
  giorni_rimanenti_mese: number;
  fatturato_giornaliero_necessario: string;
  percentuale_raggiungimento: string;
}

const MESI = [
  'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre',
];

/* ─────────────────────────── Componente ─────────────────────────── */

export const BreakEvenDashboard: React.FC = () => {
  const fmt = useFormatters();
  const { currentUO } = useUO();
  const now = new Date();

  const [unita, setUnita] = useState<Unita[]>([]);
  const [unitaId, setUnitaId] = useState('');
  const [anno, setAnno] = useState(now.getFullYear());
  const [mese, setMese] = useState(now.getMonth() + 1);
  const [data, setData] = useState<BreakEvenData | null>(null);
  const [loading, setLoading] = useState(false);

  /* Carica unità */
  useEffect(() => {
    fetch('/api/v1/unita', { headers: authHeadersGet() })
      .then((r) => r.json())
      .then((list: Unita[]) => setUnita(list))
      .catch(() => {});
  }, []);

  /* Preseleziona il ramo corrente quando cambia */
  useEffect(() => {
    if (currentUO) setUnitaId(currentUO.id);
  }, [currentUO]);

  /* Carica dati break-even */
  const carica = useCallback(async () => {
    if (!unitaId) return;
    setLoading(true);
    const params = new URLSearchParams({
      unita_organizzativa_id: unitaId,
      anno: String(anno),
      mese: String(mese),
    });
    const res = await fetch(`/api/v1/prezzi/dashboard/break-even?${params}`, {
      headers: authHeadersGet(),
    });
    if (res.ok) setData(await res.json());
    setLoading(false);
  }, [unitaId, anno, mese]);

  useEffect(() => {
    carica();
  }, [carica]);

  const pct = data ? Number(data.percentuale_raggiungimento) : 0;

  /* Colore barra progresso */
  const barColor = pct < 50
    ? 'bg-destructive'
    : pct < 80
      ? 'bg-accent'
      : 'bg-green-500 dark:bg-green-400';

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center gap-2">
        <TrendingUp className="w-5 h-5 text-primary" />
        <h1 className="text-xl font-outfit font-semibold text-foreground">
          Break-even
        </h1>
      </div>

      {/* Selettori */}
      <div className="flex gap-3 flex-wrap">
        <div>
          <label className="text-sm font-medium text-muted-foreground mb-1 block">
            Unità organizzativa
          </label>
          <select
            value={unitaId}
            onChange={(e) => setUnitaId(e.target.value)}
            className="rounded border border-border bg-card text-foreground px-3 py-1.5 text-sm w-64"
          >
            <option value="">Seleziona unità…</option>
            {unita.map((u) => (
              <option key={u.id} value={u.id}>{u.nome}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-sm font-medium text-muted-foreground mb-1 block">
            Anno
          </label>
          <select
            value={anno}
            onChange={(e) => setAnno(Number(e.target.value))}
            className="rounded border border-border bg-card text-foreground px-3 py-1.5 text-sm"
          >
            {[2024, 2025, 2026, 2027].map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-sm font-medium text-muted-foreground mb-1 block">
            Mese
          </label>
          <select
            value={mese}
            onChange={(e) => setMese(Number(e.target.value))}
            className="rounded border border-border bg-card text-foreground px-3 py-1.5 text-sm"
          >
            {MESI.map((m, i) => (
              <option key={i + 1} value={i + 1}>{m}</option>
            ))}
          </select>
        </div>
      </div>

      {!unitaId ? (
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground">
            Seleziona un&apos;unità organizzativa
          </CardContent>
        </Card>
      ) : loading ? (
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground">
            Caricamento…
          </CardContent>
        </Card>
      ) : data ? (
        <div className="space-y-4">
          {/* Card costi e fatturato */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Costi fissi mese
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-semibold text-foreground">
                  {fmt.currency(data.totale_costi_fissi_mensili)}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Fatturato realizzato
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-semibold text-foreground">
                  {fmt.currency(data.fatturato_realizzato)}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Necessario / giorno
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-semibold text-foreground">
                  {fmt.currency(data.fatturato_giornaliero_necessario)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {data.giorni_rimanenti_mese} giorni rimanenti
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Barra progresso */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Raggiungimento break-even — {MESI[mese - 1]} {anno}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">
                  {fmt.currency(data.fatturato_realizzato)} / {fmt.currency(data.fatturato_necessario)}
                </span>
                <span className="font-semibold text-foreground">
                  {fmt.number(pct, 1)}%
                </span>
              </div>
              <div className="w-full bg-muted rounded-full h-3 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-300 ${barColor}`}
                  style={{ width: `${Math.min(pct, 100)}%` }}
                />
              </div>
            </CardContent>
          </Card>
        </div>
      ) : null}
    </div>
  );
};
